// shipPhysics.ts — Client mirror of the server's PATCHED-CONIC flight model
// (products/farstars/server/sim/physics.go + ship.go). The server streams each ship's physical
// state (posLocal/velLocal/heading/thrust) at the coarse tick rate; to render smoothly — and at
// up to 1,000,000× a single frame can advance days of game-time — the client PREDICTS forward from
// the latest snapshot by re-running the SAME model: in TRANSIT, STAR-ONLY gravity + a velocity-field
// guidance law (burn hysteresis, turn-THEN-burn) + keep-out steering around the star & other planets;
// in ORBIT, an exact ANALYTIC circular orbit around the parked planet (parkingOrbit). Never an N-body
// sum. Every constant here must match the Go server exactly, or the prediction disagrees with the
// next snapshot and the ship visibly jumps.
import { Vector3 } from 'three';
import type { Galaxy, Planet, StarSystem } from '../types';
import type { Ship } from '../state/economy';
import { planetLocalPos, systemById, systemRim } from './shipCourse';

// ── constants mirrored from the server (units, game-seconds) ──
const DAY = 86400;
const ORBIT_K = 8_000_000;
const MU_STAR = (4 * Math.PI * Math.PI) / (ORBIT_K * ORBIT_K); // galaxy.StarMu
const SHIP_THRUST = 1.2e-11;
const ORBIT_SPEED_FACTOR = 2.8; // cruise = this × local circular velocity √(μ★/r)
const MIN_CRUISE_RADIUS = 0.3;
const BRAKE_SAFETY = 0.8;
const SHIP_TURN_RATE = Math.PI / (0.3 * DAY);
const BURN_START = 0.3;
const BURN_STOP = 0.06;
const THRUST_ALIGN_COS = 0.7; // engine cold only for a big reorientation (>~45°), i.e. the flip
const SUBSTEP = 0.2 * DAY;
const PLANET_MU_SCALE = 75.0;
const GATE_SPEED = 0.4 / DAY;
const ORBIT_RADIUS_FACTOR = 2.8;
const MIN_ORBIT_RADIUS = 0.12;
const STAR_KEEPOUT = 1.2; // inner exclusion radius around the star (mirror physics.go)
const PLANET_KEEPOUT_FACTOR = 1.5; // skirt a non-target planet at this × its BODY radius (mirror physics.go)
// Clamp how far ahead a single frame predicts, so a stalled/late snapshot can't make the ship
// run away (it holds at the horizon until the next snapshot re-anchors it).
const MAX_PREDICT = 6 * DAY;
const MAX_LEAD = 120 * DAY; // cap on how far ahead the guidance leads a target planet
const PATH_MAX_DAYS = 600; // cap on the planned-course horizon; covers even a slow outer cross-star swing to arrival

// Module scratch — these run every frame, so they must not allocate.
const _g = new Vector3();
const _d = new Vector3();
const _pp = new Vector3();
const _dv = new Vector3();
const _u = new Vector3();
const _tu = new Vector3();
const _vdes = new Vector3();
const _perp = new Vector3();
const _dir = new Vector3();
const _tp = new Vector3();
const _tv = new Vector3();
const _va = new Vector3();
const _vb = new Vector3();
const _p2 = new Vector3(); // scratch for the trajectory pass-1 (find arrival time)
const _v2 = new Vector3();
const _h2 = new Vector3();
const _p3 = new Vector3(); // scratch for the trajectory pass-2
const _v3 = new Vector3();
const _h3 = new Vector3();
const _seg = new Vector3(); // keep-out scratch
const _pc = new Vector3();
const _upc = new Vector3();
const _kw = new Vector3();
const _ou = new Vector3(); // parkingOrbit basis scratch
const _ov = new Vector3();
const _out = new Vector3();
const _ovt = new Vector3();
const _ZERO = new Vector3(0, 0, 0);

/** Predicted render state of a ship (parent-star frame). */
export interface ShipState {
  pos: Vector3;
  vel: Vector3;
  heading: Vector3;
  thrust: boolean;
}
export function makeShipState(): ShipState {
  return { pos: new Vector3(), vel: new Vector3(), heading: new Vector3(), thrust: false };
}

function planetMu(p: Planet): number {
  return MU_STAR * PLANET_MU_SCALE * p.radius * p.radius * p.radius;
}
function orbitRadius(p: Planet): number {
  return Math.max(p.radius * ORBIT_RADIUS_FACTOR, MIN_ORBIT_RADIUS);
}
function planetVel(p: Planet, t: number, out: Vector3): Vector3 {
  planetLocalPos(p, t - 1, _va);
  planetLocalPos(p, t + 1, _vb);
  return out.subVectors(_vb, _va).multiplyScalar(0.5);
}

// Star-only gravitational acceleration at `pos` (patched-conic; mirrors gravityAccel in physics.go).
// Planets never contribute here — they act via the analytic parking orbit + keep-out steering.
function gravityAccel(_sys: StarSystem, pos: Vector3, _t: number, out: Vector3): Vector3 {
  out.set(0, 0, 0);
  const r2 = pos.lengthSq();
  if (r2 > 1e-12) out.addScaledVector(pos, -MU_STAR / (r2 * Math.sqrt(r2)));
  return out;
}

// keepOut deflects an aim point `to` (seen from `from`) so the straight segment does not cross the
// sphere of radius `radius` at `c`: unchanged if it already clears (or the sphere is behind/beyond),
// else a point at range |to−from| along the near tangent grazing the sphere (mirrors keepOut).
function keepOut(from: Vector3, to: Vector3, c: Vector3, radius: number, out: Vector3): Vector3 {
  _seg.subVectors(to, from);
  const L = _seg.length();
  if (L < 1e-9) return out.copy(to);
  _seg.multiplyScalar(1 / L); // dHat
  _pc.subVectors(c, from);
  const pcLen = _pc.length();
  const proj = _pc.dot(_seg);
  if (proj <= 0 || proj >= L) return out.copy(to);
  if (_kw.copy(_seg).multiplyScalar(proj).sub(_pc).length() >= radius) return out.copy(to);
  if (pcLen <= radius) {
    _kw.copy(_pc).multiplyScalar(-1);
    if (_kw.lengthSq() < 1e-18) _kw.set(Math.abs(_seg.y) > 0.9 ? 1 : 0, Math.abs(_seg.y) > 0.9 ? 0 : 1, 0).cross(_seg);
    return out.copy(from).addScaledVector(_kw.normalize(), L);
  }
  _upc.copy(_pc).multiplyScalar(1 / pcLen);
  const alpha = Math.asin(radius / pcLen);
  _kw.copy(_seg).addScaledVector(_upc, -_seg.dot(_upc));
  if (_kw.lengthSq() < 1e-18) _kw.set(Math.abs(_upc.y) > 0.9 ? 1 : 0, Math.abs(_upc.y) > 0.9 ? 0 : 1, 0).cross(_upc);
  _kw.normalize();
  const ca = Math.cos(alpha);
  const sa = Math.sin(alpha);
  return out.copy(from).addScaledVector(_upc.multiplyScalar(ca).addScaledVector(_kw, sa), L);
}

// applyKeepout bends an aim away from the star and every non-target planet (mirrors applyKeepout).
// The star radius is DYNAMIC: round the star at the smaller of the {ship, aim} orbital radii so a
// cross-star transfer skirts it in its own annulus (clear of any star size), floored at STAR_KEEPOUT.
function applyKeepout(sys: StarSystem, destId: string, from: Vector3, aim: Vector3, t: number): Vector3 {
  const starR = Math.max(STAR_KEEPOUT, Math.min(from.length(), aim.length()));
  keepOut(from, aim, _ZERO, starR, aim);
  for (const q of sys.planets) {
    if (q.id === destId) continue;
    keepOut(from, aim, planetLocalPos(q, t, _pp), q.radius * PLANET_KEEPOUT_FACTOR, aim);
  }
  return aim;
}

// Desired thrust direction (into outDir) + whether a burn is WANTED, with hysteresis based on the
// current thrust state (mirrors guidance in physics.go). burning=true keeps the burn until nearly
// matched; false requires a larger error to (re)start one.
function localCruise(pos: Vector3): number {
  return ORBIT_SPEED_FACTOR * Math.sqrt(MU_STAR / Math.max(pos.length(), MIN_CRUISE_RADIUS));
}

function guidance(pos: Vector3, vel: Vector3, heading: Vector3, tp: Vector3, tv: Vector3, burning: boolean, outDir: Vector3): boolean {
  const cruise = localCruise(pos);
  _d.subVectors(tp, pos);
  const dist = _d.length();
  let vmag = Math.sqrt(2 * BRAKE_SAFETY * SHIP_THRUST * dist);
  if (vmag > cruise) vmag = cruise;
  _u.copy(_d).normalize();
  _vdes.copy(tv).addScaledVector(_u, vmag);
  _dv.subVectors(_vdes, vel);
  const thresh = (burning ? BURN_STOP : BURN_START) * cruise;
  if (_dv.length() < thresh) {
    outDir.copy(heading); // coast — hold attitude
    return false;
  }
  outDir.copy(_dv).normalize();
  return true;
}

// Rotate unit `heading` toward unit `target` by at most maxAngle, in place (mirrors slewHeading).
function slewHeading(heading: Vector3, target: Vector3, maxAngle: number): void {
  if (target.lengthSq() < 1e-18) return;
  const dot = Math.max(-1, Math.min(1, heading.dot(target)));
  const ang = Math.acos(dot);
  if (ang <= maxAngle || ang < 1e-9) {
    heading.copy(target);
    return;
  }
  _perp.copy(target).addScaledVector(heading, -dot);
  if (_perp.lengthSq() < 1e-18) {
    _perp.set(Math.abs(heading.y) > 0.9 ? 1 : 0, Math.abs(heading.y) > 0.9 ? 0 : 1, 0).cross(heading);
  }
  _perp.normalize();
  const c = Math.cos(maxAngle);
  const s = Math.sin(maxAngle);
  heading.set(heading.x * c + _perp.x * s, heading.y * c + _perp.y * s, heading.z * c + _perp.z * s);
}

// One powered sub-step: slew toward the guidance command, then fire ONLY if aligned (turn-then-
// burn), gravity + thrust via semi-implicit Euler. Returns the real thrust state (mirrors integrate).
function integrate(pos: Vector3, vel: Vector3, heading: Vector3, burning: boolean, sys: StarSystem, tp: Vector3, tv: Vector3, t: number, dt: number): boolean {
  const want = guidance(pos, vel, heading, tp, tv, burning, _dir);
  slewHeading(heading, _dir, SHIP_TURN_RATE * dt);
  const fire = want && heading.dot(_dir) >= THRUST_ALIGN_COS;
  gravityAccel(sys, pos, t, _g);
  if (fire) _g.addScaledVector(heading, SHIP_THRUST);
  vel.addScaledVector(_g, dt);
  pos.addScaledVector(vel, dt);
  return fire;
}

// parkingOrbit writes the ship's circular parking-orbit pos/vel/heading at time t, reconstructed
// closed-form from a reference state (posRef,velRef valid at tRef): the orbital-plane basis (û,v̂)
// from that state, rotated by θ=ω(t−tRef), re-centred on the planet at t so the orbit rides it.
// Radius pinned to orbitRadius(p) — exact, bound, drift-free (mirrors parkingOrbit in physics.go).
function parkingOrbit(p: Planet, posRef: Vector3, velRef: Vector3, tRef: number, t: number, outPos: Vector3, outVel: Vector3, outHead: Vector3): void {
  const r = orbitRadius(p);
  planetLocalPos(p, tRef, _pp);
  planetVel(p, tRef, _va);
  _ou.subVectors(posRef, _pp); // radial basis
  if (_ou.lengthSq() < 1e-18) _ou.set(1, 0, 0);
  _ou.normalize();
  _ov.subVectors(velRef, _va);
  _ov.addScaledVector(_ou, -_ov.dot(_ou)); // tangential basis = rel − (rel·û)û
  if (_ov.lengthSq() < 1e-18) _ov.set(Math.abs(_ou.y) > 0.9 ? 1 : 0, Math.abs(_ou.y) > 0.9 ? 0 : 1, 0).cross(_ou);
  _ov.normalize();
  const omega = Math.sqrt(planetMu(p) / (r * r * r));
  const th = omega * (t - tRef);
  const cs = Math.cos(th);
  const sn = Math.sin(th);
  _out.copy(_ou).multiplyScalar(cs).addScaledVector(_ov, sn); // rotated radial
  _ovt.copy(_ou).multiplyScalar(-sn).addScaledVector(_ov, cs); // rotated tangential
  planetLocalPos(p, t, _pp);
  planetVel(p, t, _va);
  outPos.copy(_pp).addScaledVector(_out, r);
  outVel.copy(_va).addScaledVector(_ovt, r * omega);
  outHead.copy(_ovt);
}

// Resolve the current leg's destination to the rendezvous (pos, vel) the guidance aims for: a gate
// is the rim crossing + outbound GateSpeed; a planet is its position + velocity (mirrors transitTarget).
function transitTarget(galaxy: Galaxy, sys: StarSystem, destId: string, shipPos: Vector3, t: number, outPos: Vector3, outVel: Vector3): Planet | null {
  const gt = destId.indexOf('>');
  let p: Planet | null = null;
  if (gt >= 0) {
    const nb = systemById(galaxy, destId.slice(gt + 1));
    if (!nb) return null;
    _tu.set(nb.position[0] - sys.position[0], nb.position[1] - sys.position[1], nb.position[2] - sys.position[2]).normalize();
    outPos.copy(_tu).multiplyScalar(systemRim(sys));
    outVel.copy(_tu).multiplyScalar(GATE_SPEED);
  } else {
    p = sys.planets.find((pl) => pl.id === destId) ?? null;
    if (!p) return null;
    // Lead the planet: aim where it will be after roughly the time to fly there (mirrors transitTarget).
    let lead = planetLocalPos(p, t, _pp).distanceTo(shipPos) / localCruise(shipPos);
    if (lead > MAX_LEAD) lead = MAX_LEAD;
    planetLocalPos(p, t + lead, outPos);
    planetVel(p, t + lead, outVel);
  }
  applyKeepout(sys, destId, shipPos, outPos, t); // bend the aim clear of the star + non-target planets
  return p;
}

// Predict a ship's render state at `time`, given the snapshot it last streamed (taken at
// `baseTime`). Transit: re-run the star-only integrator forward (with keep-out), stopping at arrival
// (rim / parking sphere). Orbit: evaluate the exact analytic parking orbit (runaway-proof, so no
// MAX_PREDICT clamp). Returns false if the ship isn't in-system (e.g. hyperlane).
export function shipState(galaxy: Galaxy, ship: Ship, baseTime: number, time: number, out: ShipState): boolean {
  if (ship.phase !== 'transit' && ship.phase !== 'orbit') return false;
  const sys = systemById(galaxy, ship.systemId);
  if (!sys) return false;
  out.pos.set(ship.posLocal[0], ship.posLocal[1], ship.posLocal[2]);
  out.vel.set(ship.velLocal[0], ship.velLocal[1], ship.velLocal[2]);
  out.heading.set(ship.heading[0], ship.heading[1], ship.heading[2]);
  if (out.heading.lengthSq() < 1e-12) out.heading.copy(out.vel).normalize();
  out.thrust = ship.thrust;
  if (ship.phase === 'orbit') {
    const p = sys.planets.find((pl) => pl.id === ship.originId);
    if (p) parkingOrbit(p, out.pos, out.vel, baseTime, time, out.pos, out.vel, out.heading);
    out.thrust = false;
    return true;
  }
  let dt = time - baseTime;
  if (dt < 0) dt = 0;
  if (dt > MAX_PREDICT) dt = MAX_PREDICT;
  let t = baseTime;
  const gate = ship.destId.indexOf('>') >= 0;
  const rim = systemRim(sys);
  let burning = ship.thrust;
  while (dt > 1e-9) {
    const h = Math.min(dt, SUBSTEP);
    const destPlanet = transitTarget(galaxy, sys, ship.destId, out.pos, t, _tp, _tv);
    burning = integrate(out.pos, out.vel, out.heading, burning, sys, _tp, _tv, t, h);
    out.thrust = burning;
    t += h;
    dt -= h;
    if (gate ? out.pos.length() >= rim : destPlanet && out.pos.distanceTo(planetLocalPos(destPlanet, t, _pp)) <= orbitRadius(destPlanet)) {
      break; // reached arrival — hold here (the server will transition to orbit)
    }
  }
  return true;
}

// Sample the ship's PLANNED transfer into a flat positions array (star-local frame) for the
// trajectory line — the full powered transfer from the SNAPSHOT anchor (posLocal at baseTime) all
// the way to the destination (a planet's parking sphere, or the rim for a gate leg). Anchoring at
// the snapshot makes the whole line a pure function of that snapshot: it is FROZEN between snapshots
// (Ship.tsx rebuilds it only when a new one arrives), so it can't flicker, and the ship marker
// (shipState, same anchor + integrator) rides along it. `out` must hold (segments+1)*3 floats.
export function shipPath(galaxy: Galaxy, ship: Ship, baseTime: number, segments: number, out: Float32Array): boolean {
  if (ship.phase !== 'transit') return false;
  const sys = systemById(galaxy, ship.systemId);
  if (!sys) return false;
  const gate = ship.destId.indexOf('>') >= 0;
  const rim = systemRim(sys);

  // Pass 1 — integrate the whole transfer on a pure SUBSTEP grid from the snapshot anchor; count the
  // substeps N to arrival. (Same grid the marker rides in shipState, so the line will trace it exactly.)
  _p2.set(ship.posLocal[0], ship.posLocal[1], ship.posLocal[2]);
  _v2.set(ship.velLocal[0], ship.velLocal[1], ship.velLocal[2]);
  _h2.set(ship.heading[0], ship.heading[1], ship.heading[2]);
  if (_h2.lengthSq() < 1e-12) _h2.copy(_v2).normalize();
  let burning = ship.thrust;
  let t = baseTime;
  const maxT = baseTime + PATH_MAX_DAYS * DAY;
  let n = 0;
  while (t < maxT) {
    const dp = transitTarget(galaxy, sys, ship.destId, _p2, t, _tp, _tv);
    burning = integrate(_p2, _v2, _h2, burning, sys, _tp, _tv, t, SUBSTEP);
    t += SUBSTEP;
    n++;
    if (gate ? _p2.length() >= rim : dp !== null && _p2.distanceTo(planetLocalPos(dp, t, _pp)) <= orbitRadius(dp)) break;
  }
  if (n < 1) n = 1;

  // Pass 2 — the SAME SUBSTEP grid from the same anchor; emit segments+1 vertices at evenly-spaced
  // substep indices round(i·N/segments), so the drawn polyline samples exactly the arc the marker rides.
  _p3.set(ship.posLocal[0], ship.posLocal[1], ship.posLocal[2]);
  _v3.set(ship.velLocal[0], ship.velLocal[1], ship.velLocal[2]);
  _h3.set(ship.heading[0], ship.heading[1], ship.heading[2]);
  if (_h3.lengthSq() < 1e-12) _h3.copy(_v3).normalize();
  out[0] = _p3.x;
  out[1] = _p3.y;
  out[2] = _p3.z;
  t = baseTime;
  burning = ship.thrust;
  let emitted = 1;
  for (let k = 1; k <= n; k++) {
    const dp = transitTarget(galaxy, sys, ship.destId, _p3, t, _tp, _tv);
    burning = integrate(_p3, _v3, _h3, burning, sys, _tp, _tv, t, SUBSTEP);
    t += SUBSTEP;
    void dp;
    while (emitted <= segments && Math.round((emitted * n) / segments) <= k) {
      out[emitted * 3] = _p3.x;
      out[emitted * 3 + 1] = _p3.y;
      out[emitted * 3 + 2] = _p3.z;
      emitted++;
    }
  }
  while (emitted <= segments) {
    out[emitted * 3] = _p3.x;
    out[emitted * 3 + 1] = _p3.y;
    out[emitted * 3 + 2] = _p3.z;
    emitted++;
  }
  return true;
}

// orbitCircle writes the full parking-orbit ring in PLANET-LOCAL coordinates (centred at the origin)
// — r·(cosφ û + sinφ v̂) for the orbit-plane basis derived from the streamed state. Ship.tsx sets the
// Line's position to the planet each frame, so the ring rides the moving planet (R3). It is the same
// set of points wherever the ship is on it, so it's rebuilt only when the orbit changes. `out` must
// hold (segments+1)*3 floats.
export function orbitCircle(galaxy: Galaxy, ship: Ship, baseTime: number, segments: number, out: Float32Array): boolean {
  if (ship.phase !== 'orbit') return false;
  const sys = systemById(galaxy, ship.systemId);
  if (!sys) return false;
  const p = sys.planets.find((pl) => pl.id === ship.originId);
  if (!p) return false;
  planetLocalPos(p, baseTime, _pp);
  planetVel(p, baseTime, _va);
  _ou.set(ship.posLocal[0] - _pp.x, ship.posLocal[1] - _pp.y, ship.posLocal[2] - _pp.z);
  if (_ou.lengthSq() < 1e-18) _ou.set(1, 0, 0);
  _ou.normalize();
  _ov.set(ship.velLocal[0] - _va.x, ship.velLocal[1] - _va.y, ship.velLocal[2] - _va.z);
  _ov.addScaledVector(_ou, -_ov.dot(_ou));
  if (_ov.lengthSq() < 1e-18) _ov.set(Math.abs(_ou.y) > 0.9 ? 1 : 0, Math.abs(_ou.y) > 0.9 ? 0 : 1, 0).cross(_ou);
  _ov.normalize();
  const r = orbitRadius(p);
  for (let i = 0; i <= segments; i++) {
    const phi = (2 * Math.PI * i) / segments;
    const cs = Math.cos(phi);
    const sn = Math.sin(phi);
    out[i * 3] = r * (cs * _ou.x + sn * _ov.x);
    out[i * 3 + 1] = r * (cs * _ou.y + sn * _ov.y);
    out[i * 3 + 2] = r * (cs * _ou.z + sn * _ov.z);
  }
  return true;
}
